// Item images map - each garment/service has a REAL reference photo (verified Pexels/Unsplash URLs)
// Helpers to serve compressed, right-sized variants (huge perf win vs raw 3-5MB originals)
const px = (id, w = 800) =>
  `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg?auto=compress&cs=tinysrgb&w=${w}`;
const un = (photo, w = 800) =>
  `https://images.unsplash.com/${photo}?auto=format&fit=crop&q=70&w=${w}`;

// Small thumbnail (used inside service-card preview grids)
export const thumb = (url) => {
  if (url.includes("pexels.com")) return url.replace(/w=\d+/, "w=240");
  if (url.includes("unsplash.com")) return url.replace(/w=\d+/, "w=240");
  return url;
};

const IMG = {
  // Women's — real saree/ethnic wear photos
  designer_blouses: px("37880205"),
  basic_blouses: px("27918896"),
  kurtis: px("28943474"),
  salwar_suits: px("32383364"),
  churidars: px("27719401"),
  anarkalis: px("28135787"),
  gowns: px("37054318"),
  lehengas: px("14928074"),
  party_wear: px("13155751"),
  bridal_wear: px("19609201"),

  // Men's — real kurta/sherwani/suit photos
  shirts: px("12769889"),
  pants: px("11749502"),
  kurtas: px("19267250"),
  blazers: px("12769889"),
  suits: px("11749502"),
  sherwanis: px("35542193"),
  mens_ethnic: px("32483861"),

  // Kids — reuse ethnic imagery
  frocks: px("32383364"),
  school_uniforms: px("1620760"),
  kids_ethnic: px("28135787"),
  party_dresses: px("28943474"),
  custom_designs: px("27918896"),

  // Alteration — tailoring/sewing process photos
  size_alt: px("9983579"),
  length_alt: un("photo-1739797725182-9823eace187e"),
  sleeve_alt: px("6069502"),
  zip_replace: px("6069518"),
  neck_mod: un("photo-1466027397211-20d0f2449a3f"),
  dress_repair: px("9983579"),
  restyling: un("photo-1739797725182-9823eace187e"),

  // Home Furnishing — real interior/fabric photos
  curtains: px("1148955"),
  cushion_covers: px("1866149"),
  sofa_covers: px("276583"),
  table_covers: px("1082355"),
  pillow_covers: px("545012"),

  // Hero (larger since above the fold)
  hero: px("9983579", 1200),
  tailor: un("photo-1739797725182-9823eace187e", 900),
};

export const SERVICE_CATEGORIES = [
  {
    id: "womens",
    title: "Women's Stitching",
    tagline: "Beautifully tailored outfits designed to match your style and measurements.",
    image: IMG.lehengas,
    items: [
      { name: "Designer Blouses", image: IMG.designer_blouses, price: "from ₹599" },
      { name: "Basic Blouses", image: IMG.basic_blouses, price: "from ₹399" },
      { name: "Kurtis", image: IMG.kurtis, price: "from ₹499" },
      { name: "Salwar Suits", image: IMG.salwar_suits, price: "from ₹899" },
      { name: "Churidars", image: IMG.churidars, price: "from ₹549" },
      { name: "Anarkalis", image: IMG.anarkalis, price: "from ₹1,199" },
      { name: "Gowns", image: IMG.gowns, price: "from ₹1,499" },
      { name: "Lehengas", image: IMG.lehengas, price: "from ₹2,499" },
      { name: "Party Wear", image: IMG.party_wear, price: "from ₹1,299" },
      { name: "Bridal Wear", image: IMG.bridal_wear, price: "from ₹4,999" },
    ],
  },
  {
    id: "mens",
    title: "Men's Stitching",
    tagline: "Tailored clothing with precision, comfort, and a perfect fit.",
    image: IMG.kurtas,
    items: [
      { name: "Shirts", image: IMG.shirts, price: "from ₹499" },
      { name: "Pants", image: IMG.pants, price: "from ₹549" },
      { name: "Kurtas", image: IMG.kurtas, price: "from ₹699" },
      { name: "Blazers", image: IMG.blazers, price: "from ₹1,499" },
      { name: "Suits", image: IMG.suits, price: "from ₹2,999" },
      { name: "Sherwanis", image: IMG.sherwanis, price: "from ₹2,499" },
      { name: "Ethnic Wear", image: IMG.mens_ethnic, price: "from ₹899" },
    ],
  },
  {
    id: "kids",
    title: "Kids' Stitching",
    tagline: "Comfortable, stylish clothing for every occasion.",
    image: IMG.frocks,
    items: [
      { name: "Frocks", image: IMG.frocks, price: "from ₹399" },
      { name: "School Uniforms", image: IMG.school_uniforms, price: "from ₹299" },
      { name: "Ethnic Wear", image: IMG.kids_ethnic, price: "from ₹599" },
      { name: "Party Dresses", image: IMG.party_dresses, price: "from ₹699" },
      { name: "Custom Designs", image: IMG.custom_designs, price: "on request" },
    ],
  },
  {
    id: "alteration",
    title: "Alteration Services",
    tagline: "Give your favorite clothes a perfect second life.",
    image: IMG.size_alt,
    items: [
      { name: "Size Alterations", image: IMG.size_alt, price: "from ₹149" },
      { name: "Length Adjustments", image: IMG.length_alt, price: "from ₹99" },
      { name: "Sleeve Alterations", image: IMG.sleeve_alt, price: "from ₹149" },
      { name: "Zip Replacement", image: IMG.zip_replace, price: "from ₹199" },
      { name: "Neck Modifications", image: IMG.neck_mod, price: "from ₹199" },
      { name: "Dress Repairs", image: IMG.dress_repair, price: "from ₹99" },
      { name: "Restyling Existing Outfits", image: IMG.restyling, price: "from ₹499" },
    ],
  },
  {
    id: "home_furnishing",
    title: "Home Furnishing",
    tagline: "Tailored solutions for your home.",
    image: IMG.curtains,
    items: [
      { name: "Curtains", image: IMG.curtains, price: "from ₹499/pair" },
      { name: "Cushion Covers", image: IMG.cushion_covers, price: "from ₹149" },
      { name: "Sofa Covers", image: IMG.sofa_covers, price: "from ₹1,499" },
      { name: "Table Covers", image: IMG.table_covers, price: "from ₹399" },
      { name: "Pillow Covers", image: IMG.pillow_covers, price: "from ₹129" },
    ],
  },
];

// Helper to get all item names for backward compatibility (booking modal)
export const getItemNames = (categoryId) => {
  const cat = SERVICE_CATEGORIES.find((c) => c.id === categoryId);
  return cat ? cat.items.map((i) => i.name) : [];
};

export const WHY_CHOOSE = [
  "Professional Tailors",
  "Doorstep Pickup & Delivery",
  "High-Quality Craftsmanship",
  "Transparent Pricing",
  "On-Time Delivery",
  "Customized Stitching",
  "Attention to Every Detail",
  "Customer Satisfaction Guaranteed",
];

export const STEPS = [
  { n: "01", title: "Book Your Service", desc: "Choose the stitching or alteration service you need." },
  { n: "02", title: "Schedule Pickup", desc: "Select a convenient date and time for our pickup partner." },
  { n: "03", title: "Expert Stitching", desc: "Our experienced tailors carefully stitch every outfit." },
  { n: "04", title: "Delivered to You", desc: "Your perfectly stitched outfit is delivered safely and on time." },
];

export const TESTIMONIALS = [
  {
    quote: "The fitting was absolutely perfect. The pickup and delivery service made everything effortless.",
    name: "Ananya Rao",
    role: "Bridal client",
  },
  {
    quote: "Excellent quality stitching and timely delivery. Highly recommended.",
    name: "Rohan Verma",
    role: "Suits & shirts",
  },
  {
    quote: "My designer blouse turned out exactly how I imagined. Professional work from start to finish.",
    name: "Priya Menon",
    role: "Designer blouse",
  },
];

export const FAQS = [
    { q: "Do you provide home pickup?", a: "Yes. We offer convenient doorstep pickup and delivery across our service areas." },
    { q: "Can I provide my own fabric?", a: "Absolutely. You can provide your own fabric, or we can guide you in selecting the right material." },
    { q: "How long does stitching take?", a: "Delivery time depends on the design and service selected. Standard orders are completed within the estimated timeline shared during booking." },
    { q: "Do you accept urgent orders?", a: "Yes. Express stitching is available for selected services on request." },
    { q: "Can I choose a design from the website?", a: "Yes. Open the Gallery, choose an active StichB design and select Get this design to continue through measurements, fabric and checkout." },
    { q: "When is the final price confirmed?", a: "Fixed-price services show their known total during review. Conditional orders are inspected at pickup, and the final price is confirmed with you before payment or work begins." },
    { q: "Where can I review my order details?", a: "Before checkout, the Order Summary shows your design, measurements, fabric, add-ons, pickup, delivery and known pricing with links to edit each section." },
  ];

export const TIME_SLOTS = ["9:00 AM – 12:00 PM", "12:00 PM – 3:00 PM", "3:00 PM – 6:00 PM", "6:00 PM – 8:00 PM"];

export const GALLERY = [
  { src: IMG.bridal_wear, alt: "Bridal wear" },
  { src: IMG.kurtas, alt: "Men kurta" },
  { src: "https://images.pexels.com/photos/9983579/pexels-photo-9983579.jpeg?auto=compress&cs=tinysrgb&w=800", alt: "Tailor sewing" },
  { src: "https://images.unsplash.com/photo-1739797725182-9823eace187e?auto=format&fit=crop&q=70&w=800", alt: "Sewing machine at work" },
  { src: IMG.anarkalis, alt: "Anarkali" },
  { src: IMG.sherwanis, alt: "Sherwani" },
];

export const PRICING = [
  {
    name: "Everyday",
    tag: "Basic wardrobe",
    from: 399,
    features: [
      "Basic Blouses & Kurtis",
      "Shirts, Pants, School Uniforms",
      "Size & Length Alterations",
      "5–7 day standard delivery",
      "Free doorstep pickup",
    ],
    highlight: false,
  },
  {
    name: "Signature",
    tag: "Most popular",
    from: 899,
    features: [
      "Designer Blouses, Salwar Suits, Anarkalis",
      "Blazers, Kurtas, Sherwanis",
      "Full restyling & re-fits",
      "3–5 day standard delivery",
      "Priority scheduling",
      "1 free re-fit within 7 days",
    ],
    highlight: true,
  },
  {
    name: "Couture",
    tag: "Bridal & luxury",
    from: 2499,
    features: [
      "Bridal Lehengas, Gowns, Suits",
      "Master tailor assigned",
      "In-home measurement visit",
      "Design consultation",
      "Custom fabric sourcing",
      "Unlimited fittings until perfect",
    ],
    highlight: false,
  },
];
